import { motion } from "framer-motion";
import { Hammer, Users, DollarSign, ClipboardCheck, Clock, MapPin } from "lucide-react";
import { siteConfig } from "@/lib/site-config";

const reasons = [
  {
    icon: Hammer,
    title: "Nine Trades, One Crew",
    desc: "Painting, bathrooms, flooring, plumbing, electrical, Sheetrock, tile, decks and basements — all self-performed. No waiting on a subcontractor who never shows.",
    accent: "primary" as const,
  },
  {
    icon: ClipboardCheck,
    title: "Written Scope, Written Price",
    desc: "You get the work spelled out in writing before we start. If something behind the wall changes the job, you hear about it that day — not on the invoice.",
    accent: "primary" as const,
  },
  {
    icon: DollarSign,
    title: "10% Off Over $2,000",
    desc: "Returning customers and friends of past customers save 10% on any job over $2,000. Most of our work comes from referrals, and we'd rather reward that than advertise.",
    accent: "secondary" as const,
  },
  {
    icon: Users,
    title: "Prep Nobody Sees",
    desc: "Waterproofing behind tile, level subfloors under plank, caulk and primer under paint. The invisible work is the whole reason a job still looks right in year eight.",
    accent: "primary" as const,
  },
  {
    icon: MapPin,
    title: "Local to Gwinnett",
    desc: `Based in ${siteConfig.address.city}, ${siteConfig.address.state} and working across ${siteConfig.serviceAreas.length} metro Atlanta cities. Close enough to swing by and actually look at the job.`,
    accent: "secondary" as const,
  },
  {
    icon: Clock,
    title: "6 Years and Growing",
    desc: `Owner ${siteConfig.owner} is on the tools and on the job. Six years of remodeling and painting behind the name, and a reputation we're still building on every project.`,
    accent: "primary" as const,
  },
];

const WhyChooseUs = () => {
  return (
    <section className="section-padding bg-accent relative overflow-hidden">
      <div className="absolute inset-0 gradient-mesh opacity-30" />

      <div className="container-max relative z-10">
        <div className="text-center mb-14 md:mb-16">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-secondary text-sm font-bold uppercase tracking-widest mb-3"
          >
            Why F&amp;T Renovations
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-accent-foreground mb-4 tracking-tight font-bold"
          >
            Why Choose <span className="text-gradient-primary">Us</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-accent-foreground/70 max-w-2xl mx-auto text-lg"
          >
            Six reasons metro Atlanta homeowners hand us the whole project instead of piecing it
            out to five different contractors.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {reasons.map((reason, i) => (
            <motion.div
              key={reason.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="glass-card rounded-2xl p-6 md:p-8 card-hover-lift"
            >
              <div className={`inline-flex items-center justify-center h-14 w-14 rounded-2xl mb-5 ${
                reason.accent === "secondary"
                  ? "bg-secondary/25 text-secondary"
                  : "bg-primary/25 text-primary-foreground"
              }`}>
                <reason.icon className="h-7 w-7" />
              </div>
              <h3 className="font-heading text-xl text-accent-foreground mb-2 tracking-tight font-bold">{reason.title}</h3>
              <p className="text-accent-foreground/70 text-sm leading-relaxed">{reason.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
